import type { Attributes, Counter, Histogram, Meter, UpDownCounter } from "@opentelemetry/api";

export interface OperationMetricDefinition {
  description: string;
  name: string;
  unit?: string;
}

interface OperationMetricInstruments {
  completed: Counter;
  duration: Histogram;
  inFlight?: UpDownCounter;
}

export interface CreateOperationMetricsOptions {
  completed: OperationMetricDefinition;
  duration: OperationMetricDefinition;
  inFlight?: OperationMetricDefinition;
  meter: Meter | (() => Meter);
  onRecordError: (error: unknown) => void;
}

export interface CompletedOperationMetric {
  attributes: Attributes;
  durationAttributes?: Attributes;
  durationMs?: number;
}

export type CompleteOperationMetric = (operation: CompletedOperationMetric) => boolean;

export interface OperationMetrics {
  record: (operation: CompletedOperationMetric) => void;
  run: <T>(
    attributes: Attributes,
    fn: () => Promise<T>,
    getErrorAttributes?: (error: unknown) => Attributes
  ) => Promise<T>;
  start: (inFlightAttributes: Attributes) => CompleteOperationMetric;
}

/**
 * Builds the shared counter + duration lifecycle used by integration operations.
 * Instruments are initialized lazily and every recording failure is isolated from
 * the operation being measured.
 */
export function createOperationMetrics(options: CreateOperationMetricsOptions): OperationMetrics {
  let instruments: OperationMetricInstruments | undefined;

  const getInstruments = (): OperationMetricInstruments => {
    if (instruments) return instruments;

    const meter = typeof options.meter === "function" ? options.meter() : options.meter;

    instruments = {
      completed: meter.createCounter(options.completed.name, {
        description: options.completed.description,
        ...(options.completed.unit ? { unit: options.completed.unit } : {}),
      }),
      duration: meter.createHistogram(options.duration.name, {
        description: options.duration.description,
        ...(options.duration.unit ? { unit: options.duration.unit } : {}),
      }),
      ...(options.inFlight
        ? {
            inFlight: meter.createUpDownCounter(options.inFlight.name, {
              description: options.inFlight.description,
              ...(options.inFlight.unit ? { unit: options.inFlight.unit } : {}),
            }),
          }
        : {}),
    };

    return instruments;
  };

  const reportRecordingError = (error: unknown): void => {
    try {
      options.onRecordError(error);
    } catch {
      // Observability must never alter the operation being measured.
    }
  };

  const recordSafely = (record: (current: OperationMetricInstruments) => void): void => {
    try {
      record(getInstruments());
    } catch (error) {
      reportRecordingError(error);
    }
  };

  const record = (operation: CompletedOperationMetric): void => {
    recordSafely((current) => {
      current.completed.add(1, operation.attributes);

      if (operation.durationMs !== undefined) {
        current.duration.record(operation.durationMs, operation.durationAttributes ?? operation.attributes);
      }
    });
  };

  const start = (inFlightAttributes: Attributes): CompleteOperationMetric => {
    let completed = false;

    if (options.inFlight) {
      recordSafely((current) => current.inFlight?.add(1, inFlightAttributes));
    }

    const startedAt = performance.now();

    return (operation) => {
      if (completed) return false;
      completed = true;

      recordSafely((current) => {
        current.inFlight?.add(-1, inFlightAttributes);
        current.completed.add(1, operation.attributes);
        current.duration.record(
          operation.durationMs ?? performance.now() - startedAt,
          operation.durationAttributes ?? operation.attributes
        );
      });

      return true;
    };
  };

  const run = async <T>(
    attributes: Attributes,
    fn: () => Promise<T>,
    getErrorAttributes?: (error: unknown) => Attributes
  ): Promise<T> => {
    const complete = start(attributes);

    try {
      const result = await fn();

      complete({ attributes: { ...attributes, outcome: "success" } });

      return result;
    } catch (error) {
      let classifiedAttributes: Attributes = {};

      if (getErrorAttributes) {
        try {
          classifiedAttributes = getErrorAttributes(error);
        } catch (classificationError) {
          reportRecordingError(classificationError);
        }
      }

      complete({ attributes: { ...attributes, outcome: "error", ...classifiedAttributes } });

      throw error;
    }
  };

  return { record, run, start };
}
